import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faArrowLeft, faShieldAlt, faUsers, faBroadcastTower, faRobot, faFileAlt, faComments, faCog, faCreditCard, faCheck, faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import { getToken } from '../../function/token';
import domain from '../../../reusecomponent/domain';
import Swal from 'sweetalert2';
import Preloader from '../preloader';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '../../constants/permissions';
import { SuccessToastContainer, ErrorToastContainer, WorningToastContainer } from '../../function/CustomFunction';

function CreateEditRole() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    role_name: '',
    permissions: {}
  });
  
  const navigate = useNavigate();
  const { roleId } = useParams();
  const isEditing = !!roleId;

  const [permissionStructure, setPermissionStructure] = useState({});

  // Fetch permissions from backend
  const fetchPermissions = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${domain()}/agen_role/permission_list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      // Console log the entire API response
      console.log('=== PERMISSION LIST API RESPONSE (CreateEditRole) ===');
      console.log('Full API Response:', data);
      console.log('Response Status:', data.status);
      console.log('Response Data Type:', typeof data.data);
      console.log('Response Data:', data.data);
      
      if (data.status) {
        let permissionsData = {};
        
        // Check if data is a base64 encoded string
        if (data.data && typeof data.data === 'string') {
          try {
            console.log('Attempting to decode base64 string...');
            const decodedString = atob(data.data);
            console.log('Decoded String:', decodedString);
            const decodedData = JSON.parse(decodedString);
            console.log('Parsed Decoded Data:', decodedData);
            
            // Extract permissions from the nested structure
            if (decodedData.permission) {
              console.log('Found permission key in decoded data');
              const permissionObj = JSON.parse(decodedData.permission);
              console.log('Final Permission Object:', permissionObj);
              permissionsData = permissionObj;
            }
          } catch (decodeError) {
            console.error('Error decoding base64 data:', decodeError);
          }
        } else if (data.data) {
          // Direct object structure
          console.log('Using direct object structure');
          permissionsData = data.data;
        }
        
        console.log('Final Permissions Data to be set:', permissionsData);
        setPermissionStructure(permissionsData);
      } else {
        console.error('Failed to fetch permissions:', data.message);
        // Fallback to default structure if API fails
        setPermissionStructure({
          dashboard: {
            icon: faShieldAlt,
            title: 'Dashboard',
            permissions: {
              purchase_credit: 'Purchase Credit',
              connect_whatsapp: 'Connect WhatsApp'
            }
          }
        });
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Fallback to default structure if API fails
      setPermissionStructure({
        dashboard: {
          icon: faShieldAlt,
          title: 'Dashboard',
          permissions: {
            purchase_credit: 'Purchase Credit',
            connect_whatsapp: 'Connect WhatsApp'
          }
        }
      });
    }
  };

  // Icon mapping for different permission categories
  const getIconForCategory = (categoryKey) => {
    const iconMap = {
      dashboard: faShieldAlt,
      broadcast_campaign: faBroadcastTower,
      users: faUsers,
      chatbot: faRobot,
      templates: faFileAlt,
      whatsapp_profile: faComments,
      whatsapp_chats: faComments,
      settings: faCog,
      billing: faCreditCard
    };
    return iconMap[categoryKey] || faShieldAlt;
  };

  // Initialize default permissions structure (all false)
  const getDefaultPermissions = () => {
    const defaultPermissions = {};
    
    const processCategory = (categoryKey, category) => {
      // Handle direct permissions in category
      if (category && typeof category === 'object') {
        Object.keys(category).forEach(key => {
          if (typeof category[key] === 'boolean') {
            // This is a direct permission - use unique key with category prefix
            defaultPermissions[`${categoryKey}_${key}`] = false;
          } else if (typeof category[key] === 'object') {
            // This is a subcategory
            Object.keys(category[key]).forEach(subKey => {
              if (typeof category[key][subKey] === 'boolean') {
                // This is a permission in subcategory - use unique key with full path
                defaultPermissions[`${categoryKey}_${key}_${subKey}`] = false;
              }
            });
          }
        });
      }
    };

    Object.keys(permissionStructure).forEach(categoryKey => {
      processCategory(categoryKey, permissionStructure[categoryKey]);
    });

    return defaultPermissions;
  };

  // Fetch role data for editing
  const fetchRoleData = async () => {
    if (!isEditing) {
      setFormData({
        role_name: '',
        permissions: getDefaultPermissions()
      });
      setLoading(false);
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${domain()}/agen_role/${btoa(roleId)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      console.log('=== ROLE DATA API RESPONSE ===');
      console.log('Full API Response:', data);
      console.log('Response Status:', data.status);
      console.log('Response Data:', data.data);
      
      if (data.status) {
        let roleData = data.data;
        
        // Check if data is encrypted (base64 encoded string)
        if (typeof roleData === 'string') {
          try {
            console.log('Attempting to decode encrypted role data...');
            const decodedString = atob(roleData);
            console.log('Decoded String:', decodedString);
            roleData = JSON.parse(decodedString);
            console.log('Parsed Decoded Role Data:', roleData);
          } catch (decodeError) {
            console.error('Error decoding role data:', decodeError);
            Swal.fire(
              'Error!',
              'Failed to decode role data. Please try again.',
              'error'
            );
            navigate('/client-area/whatsapp/user-roles');
            return;
          }
        }
        
        const roleName = roleData.role_name || roleData.name || '';
        
        // Parse existing permissions or use default
        let existingPermissions = getDefaultPermissions();
        if (roleData.permissions) {
          try {
            console.log('Raw permissions data:', roleData.permissions);
            
            if (typeof roleData.permissions === 'string') {
              // Parse the JSON string
              const parsedPermissions = JSON.parse(roleData.permissions);
              console.log('Parsed permissions:', parsedPermissions);
              existingPermissions = parsedPermissions;
            } else {
              existingPermissions = roleData.permissions;
            }
          } catch (e) {
            console.error('Error parsing permissions:', e);
            console.log('Using default permissions due to parsing error');
          }
        }
        
        console.log('Final form data to be set:', {
          role_name: roleName,
          permissions: existingPermissions
        });
        
        setEditingRole(roleData);
        setFormData({
          role_name: roleName,
          permissions: existingPermissions
        });
      } else {
        Swal.fire(
          'Error!',
          `Failed to fetch role details: ${data.message}`,
          'error'
        );
        navigate('/client-area/whatsapp/user-roles');
      }
    } catch (error) {
      console.error('Error fetching role data:', error);
      Swal.fire(
        'Error!',
        'Error fetching role details. Please try again.',
        'error'
      );
      navigate('/client-area/whatsapp/user-roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([fetchRoleData(), fetchPermissions()]);
    };
    initializeData();
  }, [roleId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.role_name || formData.role_name.trim() === '') {
      Swal.fire(
        'Error!',
        'Role name is required.',
        'error'
      );
      return;
    }
    
    setSaving(true);
    
    try {
      const token = getToken();
      const url = isEditing 
        ? `${domain()}/agen_role/${btoa(roleId)}`
        : `${domain()}/agen_role`;
      
      // Prepare the request body with hierarchical structure
      const requestBody = {
        role_name: formData.role_name.trim(),
        permissions: formData.permissions
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      if (data.status) {
        Swal.fire(
          isEditing ? 'Updated!' : 'Created!',
          `Role has been ${isEditing ? 'updated' : 'created'} successfully.`,
          'success'
        ).then(() => {
          navigate('/client-area/whatsapp/user-roles');
        });
      } else {
        Swal.fire(
          'Error!',
          `Failed to ${isEditing ? 'update' : 'create'} role: ${data.message}`,
          'error'
        );
      }
    } catch (error) {
      Swal.fire(
        'Error!',
        `Error ${isEditing ? 'updating' : 'creating'} role. Please try again.`,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionChange = (permissionKey) => {
    console.log('=== PERMISSION CHANGE DEBUG ===');
    console.log('Clicked permission key:', permissionKey);
    console.log('All current permissions:', Object.keys(formData.permissions));
    
    setFormData(prev => {
      const newPermissions = { ...prev.permissions };
      const currentValue = newPermissions[permissionKey];
      
      // Toggle the current permission
      newPermissions[permissionKey] = !currentValue;
    
      
      // Find WhatsApp chat permission keys with multiple matching strategies
      const allChatKeys = Object.keys(newPermissions).filter(key => {
        const matches = (key.includes('whatsapp_chats') && key.includes('all') && !key.includes('selected') && !key.includes('new')) ||
                       (key.includes('whatsapp') && key.includes('all') && !key.includes('selected') && !key.includes('new'));
        if (matches) console.log('🎯 Found ALL CHAT key:', key);
        return matches;
      });
      
      const selectedChatKeys = Object.keys(newPermissions).filter(key => {
        const matches = (key.includes('whatsapp_chats') && key.includes('selected')) ||
                       (key.includes('whatsapp') && key.includes('selected'));
        if (matches) console.log('🎯 Found SELECTED CHAT key:', key);
        return matches;
      });
      
      const newChatKeys = Object.keys(newPermissions).filter(key => {
        const matches = (key.includes('whatsapp_chats') && key.includes('new')) ||
                       (key.includes('whatsapp') && key.includes('new'));
        if (matches) console.log('🎯 Found NEW CHAT key:', key);
        return matches;
      });
      const chatAssignKeys = Object.keys(newPermissions).filter(key => 
        key.includes('whatsapp_chats') && key.includes('chat_assign')
      );
      const chatTransferKeys = Object.keys(newPermissions).filter(key => 
        key.includes('whatsapp_chats') && key.includes('chat_transfer')
      );

      

      // Rule 1: All Chat vs Selected/New Chat exclusivity
      if (permissionKey.includes('whatsapp')) {
        console.log('🔥 WhatsApp permission changed:', permissionKey, 'new value:', newPermissions[permissionKey]);
        
        if (newPermissions[permissionKey]) {
          // A WhatsApp permission is being ENABLED
          console.log('🎯 Matched keys - All:', allChatKeys, 'Selected:', selectedChatKeys, 'New:', newChatKeys);
          
          if (allChatKeys.includes(permissionKey)) {
            // All Chat is being enabled - FORCE disable Selected and New Chat Only
            console.log('🔥 ALL CHAT ENABLED - DISABLING OTHERS');
            selectedChatKeys.forEach(key => {
              console.log('❌ Force disabling Selected Chat:', key, 'was:', newPermissions[key]);
              newPermissions[key] = false;
            });
            newChatKeys.forEach(key => {
              console.log('❌ Force disabling New Chat:', key, 'was:', newPermissions[key]);
              newPermissions[key] = false;
            });
            WorningToastContainer('All Chat selected. Selected Chat Only and New Chat Only have been automatically deselected.');
            
          } else if (selectedChatKeys.includes(permissionKey) || newChatKeys.includes(permissionKey)) {
            // Selected or New Chat Only is being enabled - FORCE disable All Chat
            console.log('🔥 SELECTED/NEW CHAT ENABLED - DISABLING ALL CHAT');
            allChatKeys.forEach(key => {
              console.log('❌ Force disabling All Chat:', key, 'was:', newPermissions[key]);
              newPermissions[key] = false;
            });
            WorningToastContainer('Selected Chat Only or New Chat Only selected. All Chat has been automatically deselected.');
          }
        }
      }
      
      // Rule 2: Chat Assign dependency - requires All Chats
      if (chatAssignKeys.includes(permissionKey)) {
        if (newPermissions[permissionKey]) {
          // Chat Assign is being enabled - ensure All Chat is enabled
          const allChatsEnabled = allChatKeys.some(key => newPermissions[key]);
          
          if (!allChatsEnabled) {
            console.log('Chat Assign enabled, auto-enabling All Chat');
            // Automatically enable All Chats first
            allChatKeys.forEach(key => {
              newPermissions[key] = true;
              console.log('Auto-enabled All Chat:', key);
            });
            // Disable Selected and New Chat Only
            selectedChatKeys.forEach(key => {
              newPermissions[key] = false;
              console.log('Auto-disabled:', key);
            });
            newChatKeys.forEach(key => {
              newPermissions[key] = false;
              console.log('Auto-disabled:', key);
            });
            WorningToastContainer('Chat Assign requires All Chat permission. All Chat has been automatically enabled and other chat permissions disabled.');
          }
        } else {
          // Chat Assign is being disabled - check if it was enabled due to All Chat requirement
          console.log('Chat Assign disabled explicitly by user');
        }
      }

      // Rule 2a: Validate Chat Assign cannot be enabled without All Chat (additional safety check)
      if (chatAssignKeys.some(key => newPermissions[key])) {
        const allChatsEnabled = allChatKeys.some(key => newPermissions[key]);
        if (!allChatsEnabled) {
          console.log('ERROR: Chat Assign found enabled without All Chat - fixing...');
          // Force disable Chat Assign if All Chat is not enabled
          chatAssignKeys.forEach(key => {
            if (newPermissions[key]) {
              newPermissions[key] = false;
              console.log('Force disabled Chat Assign:', key);
            }
          });
          ErrorToastContainer('Chat Assign Enable cannot be selected without All Chat permission.');
        }
      }
      
      // Rule 3: Chat Transfer dependency - requires at least one chat permission
      if (chatTransferKeys.includes(permissionKey) && newPermissions[permissionKey]) {
        const chatPermissionsEnabled = allChatKeys.some(key => newPermissions[key]) ||
                                      selectedChatKeys.some(key => newPermissions[key]) ||
                                      newChatKeys.some(key => newPermissions[key]);
        
        if (!chatPermissionsEnabled) {
          console.log('Chat Transfer attempted without chat permissions, blocking');
          // If no chat permissions are enabled, don't allow Chat Transfer
          newPermissions[permissionKey] = false;
          ErrorToastContainer('Chat Transfer requires at least one chat permission (All Chat, Selected Chat Only, or New Chat Only) to be enabled first.');
        }
      }
      
      // Rule 4: If All Chat is being disabled, also disable Chat Assign
      if (allChatKeys.includes(permissionKey) && !newPermissions[permissionKey]) {
        console.log('All Chat disabled, disabling Chat Assign');
        chatAssignKeys.forEach(key => {
          newPermissions[key] = false;
          console.log('Disabled Chat Assign:', key);
        });
        if (chatAssignKeys.some(key => formData.permissions[key])) {
          WorningToastContainer('All Chat disabled. Chat Assign has been automatically disabled as it requires All Chat permission.');
        }
      }
      
      // Rule 5: If all chat permissions are being disabled, also disable Chat Transfer
      if ((allChatKeys.includes(permissionKey) || selectedChatKeys.includes(permissionKey) || newChatKeys.includes(permissionKey)) && 
          !newPermissions[permissionKey]) {
        
        const remainingChatPermissions = allChatKeys.some(key => newPermissions[key] && key !== permissionKey) ||
                                        selectedChatKeys.some(key => newPermissions[key] && key !== permissionKey) ||
                                        newChatKeys.some(key => newPermissions[key] && key !== permissionKey);
        
        if (!remainingChatPermissions) {
          console.log('No chat permissions remaining, disabling Chat Transfer');
          const wasTransferEnabled = chatTransferKeys.some(key => formData.permissions[key]);
          chatTransferKeys.forEach(key => {
            newPermissions[key] = false;
            console.log('Disabled Chat Transfer:', key);
          });
          if (wasTransferEnabled) {
            WorningToastContainer('All chat permissions disabled. Chat Transfer has been automatically disabled.');
          }
        }
      }
      
      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };

  const getSelectedPermissionsCount = () => {
    return Object.values(formData.permissions).filter(Boolean).length;
  };

  const getTotalPermissionsCount = () => {
    return Object.keys(formData.permissions).length;
  };

  if (loading) {
    return (
      <div className="main-panel w-100">
        <div className="content-wrapper">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  <p className="mt-2">Loading role data...</p>
                  <Preloader action={loading} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-panel w-100">
      <Preloader action={saving} />
      <div className="content-wrapper">
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <button
                      className="btn btn-outline-secondary me-3"
                      onClick={() => navigate('/client-area/whatsapp/user-roles')}
                      style={{
                        borderRadius: '0.5rem',
                        padding: '0.5rem 1rem'
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                      Back to Roles
                    </button>
                    <div>
                      <h4 className="mb-1" style={{ color: '#2c3e50', fontWeight: '600' }}>
                        {isEditing ? 'Edit Role' : 'Create New Role'}
                      </h4>
                      <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                        {isEditing ? 'Modify role permissions and settings' : 'Define a new role with specific permissions'}
                      </p>
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: '#e9ecef',
                    padding: '0.5rem 1rem',
                    borderRadius: '2rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#6c757d'
                  }}>
                    {getSelectedPermissionsCount()} of {getTotalPermissionsCount()} permissions selected
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Role Name Section */}
                <div className="col-12 mb-4">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
                    <div className="card-header bg-white border-bottom" style={{ borderRadius: '20px 20px 0 0' }}>
                      <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-2" style={{ color: '#075E54' }} />
                        Basic Information
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="form-group">
                        <label htmlFor="roleName" style={{ 
                          fontWeight: '600', 
                          color: '#2c3e50',
                          marginBottom: '0.5rem'
                        }}>
                          Role Name <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="roleName"
                          value={formData.role_name}
                          onChange={(e) => setFormData({ ...formData, role_name: e.target.value })}
                          required
                          style={{
                            borderRadius: '0.5rem',
                            border: '2px solid #e9ecef',
                            padding: '0.75rem 1rem',
                            fontSize: '1rem',
                            transition: 'border-color 0.3s ease'
                          }}
                          placeholder="Enter role name..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Permissions Section */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
                    <div className="card-header bg-white border-bottom" style={{ borderRadius: '20px 20px 0 0' }}>
                      <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                        <FontAwesomeIcon icon={faUsers} className="mr-2" style={{ color: '#075E54' }} />
                        Permissions Configuration
                      </h5>
                    </div>
                    <div className="card-body">
                                             <div style={{
                         maxHeight: '70vh',
                         overflowY: 'auto',
                         padding: '1rem'
                       }}>
                         {Object.keys(permissionStructure).length > 0 ? (
                           Object.entries(permissionStructure).map(([categoryKey, category]) => (
                             <div key={categoryKey} className="mb-4">
                               {/* Category Header */}
                               <div style={{
                                 backgroundColor: '#f8f9fa',
                                 padding: '1rem',
                                 borderRadius: '0.5rem',
                                 marginBottom: '1rem',
                                 borderLeft: '4px solid #075E54'
                               }}>
                                 <div className="d-flex align-items-center">
                                   <FontAwesomeIcon 
                                     icon={getIconForCategory(categoryKey)} 
                                     style={{ color: '#075E54', marginRight: '0.75rem' }}
                                   />
                                   <h6 style={{ 
                                     margin: '0', 
                                     fontWeight: '600', 
                                     color: '#2c3e50' 
                                   }}>
                                     {categoryKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                   </h6>
                                 </div>
                               </div>

                                                               {/* Direct Permissions */}
                                <div className="row mb-3">
                                  {Object.entries(category).map(([key, value]) => {
                                    if (typeof value === 'boolean') {
                                      // This is a direct permission
                                      return (
                                        <div key={key} className="col-md-6 col-lg-4 mb-2">
                                          <div className="custom-control custom-checkbox">
                                            <input
                                              type="checkbox"
                                              className="custom-control-input"
                                              id={`permission-${categoryKey}-${key}`}
                                              checked={formData.permissions[`${categoryKey}_${key}`] || false}
                                              onChange={() => handlePermissionChange(`${categoryKey}_${key}`)}
                                            />
                                            <label 
                                              className="custom-control-label" 
                                              htmlFor={`permission-${categoryKey}-${key}`}
                                              style={{ 
                                                cursor: 'pointer',
                                                fontWeight: '500',
                                                color: '#495057'
                                              }}
                                            >
                                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </label>
                                          </div>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>

                                {/* Subcategories */}
                                {Object.entries(category).map(([key, value]) => {
                                  if (typeof value === 'object' && value !== null && typeof value !== 'boolean') {
                                    // This is a subcategory
                                    return (
                                      <div key={key} className="mb-3">
                                        <h6 style={{ 
                                          color: '#6c757d', 
                                          fontWeight: '600',
                                          marginBottom: '0.75rem',
                                          paddingLeft: '1rem',
                                          borderLeft: '2px solid #dee2e6'
                                        }}>
                                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </h6>
                                        <div className="row" style={{ paddingLeft: '1rem' }}>
                                          {Object.entries(value).map(([permKey, permValue]) => (
                                            <div key={permKey} className="col-md-6 col-lg-4 mb-2">
                                              <div className="custom-control custom-checkbox">
                                                <input
                                                  type="checkbox"
                                                  className="custom-control-input"
                                                  id={`permission-${categoryKey}-${key}-${permKey}`}
                                                  checked={formData.permissions[`${categoryKey}_${key}_${permKey}`] || false}
                                                  onChange={() => handlePermissionChange(`${categoryKey}_${key}_${permKey}`)}
                                                />
                                                <label 
                                                  className="custom-control-label" 
                                                  htmlFor={`permission-${categoryKey}-${key}-${permKey}`}
                                                  style={{ 
                                                    cursor: 'pointer',
                                                    fontWeight: '500',
                                                    color: '#495057'
                                                  }}
                                                >
                                                  {permKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                </label>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })}
                             </div>
                           ))
                         ) : (
                           <div className="text-center py-4">
                             <div style={{ color: '#6c757d' }}>
                               <FontAwesomeIcon icon={faShieldAlt} size="2x" className="mb-3" />
                               <p className="mb-0">No permissions available</p>
                               <small>Please check the API endpoint or contact administrator.</small>
                             </div>
                           </div>
                         )}
                       </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="row mt-4">
                <div className="col-12">
                  <div className="card shadow-sm" style={{ border: 'none', borderRadius: '0.75rem' }}>
                    <div className="card-body">
                      <div className="d-flex justify-content-end gap-3">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => navigate('/client-area/whatsapp/user-roles')}
                          style={{
                            borderRadius: '0.5rem',
                            padding: '0.75rem 1.5rem',
                            fontWeight: '500'
                          }}
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={saving}
                          style={{
                            borderRadius: '0.5rem',
                            padding: '0.75rem 1.5rem',
                            fontWeight: '500',
                            backgroundColor: '#075E54',
                            borderColor: '#075E54'
                          }}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              {isEditing ? 'Updating...' : 'Creating...'}
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={isEditing ? faEdit : faSave} className="mr-2" />
                              {isEditing ? 'Update Role' : 'Create Role'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateEditRole;
